/**
 * 按钮权限同步工具函数
 */
import type { ParsedPermissionItem, PolicyItem, UpdateRoleParams } from './types';

/**
 * 解析权限标识字符串
 * @param key 权限标识，格式如 '["group"].resource.type' 或 '["group"].resource/subresource.type'
 * @returns 解析后的权限项
 */
export function parsePermissionKey(key: string): ParsedPermissionItem | null {
  if (!key) return null;

  // 匹配格式: ["group"].resource.type 或 ['group'].resource.type
  const match = key.match(/^\[["']?([^\]"']+)["']?\]\.([^.]+)\.([^.]+)$/);
  if (match) {
    return {
      group: match[1],
      resource: match[2],
      type: match[3],
    };
  }
  return null;
}

/**
 * 构建权限标识字符串
 * @param group API 组
 * @param resource 资源名称
 * @param type 操作类型
 * @returns 权限标识字符串
 */
export function buildPermissionKey(group: string, resource: string, type: string): string {
  return `["${group}"].${resource}.${type}`;
}

/**
 * 将权限策略数组转换为更新参数格式
 * @param policies 权限策略数组
 * @returns 更新参数
 */
export function transformPoliciesToUpdateParams(policies: PolicyItem[]): UpdateRoleParams {
  const groupMap: Record<string, Record<string, Set<string>>> = {};

  policies.forEach((policy) => {
    const { group, resource, verb } = policy;
    if (!groupMap[group]) {
      groupMap[group] = {};
    }
    if (!groupMap[group][resource]) {
      groupMap[group][resource] = new Set();
    }
    groupMap[group][resource].add(verb);
  });

  const groups = Object.entries(groupMap).map(([group, resources]) => ({
    group,
    resources: Object.entries(resources).map(([resource, verbs]) => ({
      resource,
      verbs: Array.from(verbs),
    })),
  }));

  return { groups, namespace: '*' };
}

/**
 * 将权限标识转换为策略项
 * @param key 权限标识字符串
 * @param domInfo 环境信息
 * @returns 策略项
 */
export function permissionKeyToPolicy(key: string, domInfo: { module: string; cluster?: string; workspace?: string }): PolicyItem | null {
  const parsed = parsePermissionKey(key);
  if (!parsed) return null;

  return {
    api: '*',
    datacenter: domInfo.cluster || '-',
    workspace: domInfo.workspace || '-',
    group: parsed.group,
    namespace: '*',
    resource: parsed.resource,
    verb: parsed.type,
  };
}

/**
 * 合并权限策略（去重）
 * @param existingPolicies 现有策略
 * @param newPolicies 新策略
 * @returns 合并后的策略
 */
export function mergePolicies(existingPolicies: PolicyItem[], newPolicies: PolicyItem[]): PolicyItem[] {
  const policySet = new Set<string>();
  const result: PolicyItem[] = [];

  // 添加现有策略
  existingPolicies.forEach((policy) => {
    const key = `${policy.api}|${policy.datacenter}|${policy.workspace}|${policy.group}|${policy.namespace}|${policy.resource}|${policy.verb}`;
    if (!policySet.has(key)) {
      policySet.add(key);
      result.push(policy);
    }
  });

  // 添加新策略
  newPolicies.forEach((policy) => {
    const key = `${policy.api}|${policy.datacenter}|${policy.workspace}|${policy.group}|${policy.namespace}|${policy.resource}|${policy.verb}`;
    if (!policySet.has(key)) {
      policySet.add(key);
      result.push(policy);
    }
  });

  return result;
}

/**
 * 检查权限策略是否匹配
 * @param policy 策略项
 * @param group API 组
 * @param resource 资源名称
 * @param verb 操作类型
 * @returns 是否匹配
 */
export function isPolicyMatch(policy: PolicyItem, group: string, resource: string, verb: string): boolean {
  return (
    (policy.group === '*' || policy.group === group) &&
    (policy.resource === '*' || policy.resource === resource) &&
    (policy.verb === '*' || policy.verb === verb)
  );
}

/**
 * 从策略列表中查找匹配的策略
 * @param policies 策略列表
 * @param group API 组
 * @param resource 资源名称
 * @param verb 操作类型
 * @returns 是否存在匹配策略
 */
export function hasMatchingPolicy(policies: PolicyItem[], group: string, resource: string, verb: string): boolean {
  return policies.some((policy) => isPolicyMatch(policy, group, resource, verb));
}

/**
 * 获取当前用户的角色名称
 * 从 userInfo 中获取当前用户的角色信息
 * @param userInfo 用户信息
 * @param domInfo 环境信息
 * @returns 角色名称
 */
export function getCurrentRoleName(userInfo: any, domInfo: { module: string; cluster?: string; workspace?: string }): string | null {
  if (!userInfo) return null;

  // 根据不同模块获取角色名称
  // 这里需要根据实际的用户信息结构来调整
  if (domInfo.module === 'tenant' && domInfo.workspace) {
    return userInfo?.workspaceRoles?.[domInfo.workspace]?.[0] || null;
  }

  if (domInfo.module === 'data-center' && domInfo.cluster) {
    return userInfo?.clusterRoles?.[domInfo.cluster]?.[0] || null;
  }

  if (domInfo.module === 'system') {
    return userInfo?.systemRoles?.[0] || null;
  }

  return null;
}
